import React, { useState, useEffect } from 'react';
import Footer from './Footer';
import { works } from '../data/works';
import TechnologyChips from './TechnologyChips';

const CaseStudyModal = ({ project, onClose }) => {
  const imageSrc = Array.isArray(project?.src) ? project.src[0] : project?.src || '';

  useEffect(() => {
    const handleEscape = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-study-title"
    >
      <div
        className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-slate-900 rounded-2xl border border-accent/20 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
          aria-label="Close modal"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="p-8">
          {imageSrc && (
            <div className="relative mb-6 rounded-xl overflow-hidden">
              <img src={imageSrc} alt={project?.title} className="w-full h-48 object-cover" />
              <div className="absolute top-4 left-4">
                <span className="bg-accent text-background-dark px-3 py-1 rounded-full text-xs font-semibold">
                  {project?.category}
                </span>
              </div>
            </div>
          )}
          <h2 id="case-study-title" className="text-2xl font-bold text-slate-100 mb-2">
            {project?.title}
          </h2>
          <span className="text-sm text-slate-500">{project?.date}</span>
          <p className="text-slate-400 mt-4 leading-relaxed">{project?.desc}</p>
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-slate-400 mb-2 uppercase tracking-wider">Technologies</h3>
            <TechnologyChips {...project} />
          </div>
          {project?.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 mt-6 bg-accent text-background-dark hover:bg-accent/90 px-6 py-3 rounded-xl font-semibold transition-all duration-300"
            >
              <span>Visit Site</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [modalProject, setModalProject] = useState(null);
  return (
    <div className="min-h-screen bg-background-dark text-slate-100 font-display">
      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-accent/5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/20 text-accent px-6 py-3 rounded-full text-sm font-semibold mb-8">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              <span>Systems & Operational Intelligence</span>
              <span className="w-2 h-2 bg-accent rounded-full"></span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Portfolio
            </h1>
            <p className="text-xl lg:text-2xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
              Systems that move the world — from human experience to resilient infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Projects */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {works.map((project, index) => (
              <div key={project.id} className="group">
                <div className="bg-slate-800/30 rounded-2xl overflow-hidden border border-accent/10 hover:border-accent/20 transition-all duration-500 hover:shadow-2xl">
                  {/* Project Image */}
                  <div className="relative overflow-hidden">
                    <img 
                      src={project.src[0]} 
                      alt={project.title}
                      className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-accent text-background-dark px-3 py-1 rounded-full text-xs font-semibold">
                        {project.category}
                      </span>
                    </div>
                    <div className="absolute bottom-4 right-4">
                      <span className="bg-black/50 text-white px-3 py-1 rounded-full text-xs">
                        {project.date}
                      </span>
                    </div>
                  </div>

                  {/* Project Content */}
                  <div className="p-8">
                    <h3 className="text-2xl lg:text-3xl font-bold text-slate-100 mb-4 group-hover:text-accent transition-colors duration-300">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-accent focus:text-accent focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-background-dark rounded"
                      >
                        {project.title}
                      </a>
                    </h3>
                    <p className="text-slate-400 text-lg leading-relaxed mb-6">
                      {project.desc}
                    </p>

                    {/* Technologies */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wider">
                        Technologies
                      </h4>
                      <TechnologyChips {...project} />
                    </div>

                    {/* Project Links */}
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-accent text-background-dark hover:bg-accent/90 px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-lg"
                      >
                        <span>View Project</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <button
                        type="button"
                        onClick={() => setModalProject(project)}
                        className="inline-flex items-center space-x-2 border border-accent/30 text-accent hover:bg-accent/5 px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                      >
                        <span>View Case Study</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-accent/5 rounded-3xl p-12 border border-accent/10">
              <div className="text-center">
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-8">
                  Infrastructure you can trust
                </h2>
                <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
                  We don't chase trends; we establish standards — composable, auditable, and principled.
                  From mobile apps that empower better choices to enterprise standards guiding AI and systems interoperability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="bg-accent/5 rounded-3xl p-12 border border-accent/10">
              <h3 className="text-3xl lg:text-4xl font-bold text-slate-100 mb-6">
                Built for leaders
              </h3>
              <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                Systems that serve humans — for those who prefer clarity over complexity and value sovereignty over dependency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center space-x-2 bg-accent text-background-dark hover:bg-accent/90 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  <span>Start a Project</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
                <a
                  href="/about"
                  className="inline-flex items-center space-x-2 border border-accent/30 text-accent hover:bg-accent/5 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105"
                >
                  <span>Learn More</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {modalProject && (
        <CaseStudyModal project={modalProject} onClose={() => setModalProject(null)} />
      )}

      <Footer />
    </div>
  );
};

export default Portfolio;
